import React, { useEffect, useMemo, useState } from "react";
import { getLanguageCopy, LANGUAGE_OPTIONS, normalizeLanguage } from "../services/language";
import { TERMS_CONTENT_MARKDOWN, TERMS_MODAL_COPY } from "../constants/termsAndConditions";

const normalizeMarkdownLine = (line) =>
  String(line || "")
    .trim()
    .replace(/^#{1,6}\s*/, "")
    .trim();

const parseTermsMarkdown = (markdown) => {
  const blocks = [];
  let currentList = [];

  const flushList = () => {
    if (currentList.length > 0) {
      blocks.push({ type: "list", items: currentList });
      currentList = [];
    }
  };

  String(markdown || "")
    .split(/\r?\n/)
    .forEach((rawLine) => {
      const line = String(rawLine || "").trim();
      if (!line || /^-{2,}$/.test(line)) return;

      const bulletMatch = /^\*\s+(.+)$/.exec(line);
      if (bulletMatch) {
        currentList.push(bulletMatch[1].trim());
        return;
      }

      flushList();
      const headingMatch = /^(#{1,6})\s*(.+)$/.exec(line);
      if (headingMatch) {
        blocks.push({
          type: "heading",
          level: headingMatch[1].length,
          text: normalizeMarkdownLine(line),
        });
        return;
      }

      blocks.push({ type: "paragraph", text: line });
    });

  flushList();
  return blocks;
};

const renderTextWithLinks = (text) => {
  const parts = String(text || "").split(/(https?:\/\/[^\s*]+)/g);
  return parts.map((part, index) => {
    if (/^https?:\/\//.test(part)) {
      return (
        <a
          key={`${part}-${index}`}
          href={part}
          target="_blank"
          rel="noreferrer"
          className="text-[#ffd66b] underline-offset-2 hover:underline"
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

const renderInlineMarkdown = (text) =>
  String(text || "")
    .split(/(\*\*[^*]+\*\*)/g)
    .map((part, index) => {
      const strongMatch = /^\*\*(.+)\*\*$/.exec(part);
      if (strongMatch) {
        return (
          <strong key={`${strongMatch[1]}-${index}`} className="font-semibold text-[#fff4c7]">
            {renderTextWithLinks(strongMatch[1])}
          </strong>
        );
      }
      return <React.Fragment key={`${part}-${index}`}>{renderTextWithLinks(part)}</React.Fragment>;
    });

const TermsAcceptanceModal = ({
  open,
  mode = "guest",
  loading = false,
  language = "es",
  allowCancel = true,
  onAccept,
  onCancel,
}) => {
  const [accepted, setAccepted] = useState(false);
  const [termsLanguage, setTermsLanguage] = useState(() =>
    normalizeLanguage(language)
  );
  const t = useMemo(
    () => (key) => getLanguageCopy(termsLanguage, TERMS_MODAL_COPY[key], key),
    [termsLanguage]
  );

  const termsBlocks = useMemo(() => {
    const markdown =
      TERMS_CONTENT_MARKDOWN[termsLanguage] ||
      TERMS_CONTENT_MARKDOWN.en ||
      TERMS_CONTENT_MARKDOWN.es ||
      "";
    return parseTermsMarkdown(markdown);
  }, [termsLanguage]);

  useEffect(() => {
    const defaultTermsLanguage = normalizeLanguage(language);
    setTermsLanguage(defaultTermsLanguage);
    if (!open) {
      setAccepted(false);
    }
  }, [language, mode, open]);

  if (!open) return null;

  const intro =
    mode === "register"
      ? t("registerIntro")
      : mode === "authenticated"
      ? t("authenticatedIntro")
      : t("guestIntro");

  const acceptLabel =
    mode === "register"
      ? t("acceptLabelRegister")
      : mode === "authenticated"
      ? t("acceptLabelAuthenticated")
      : t("acceptLabelGuest");

  return (
    <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/80 px-4 py-4">
      <div className="w-full max-w-2xl rounded-lg border border-[#6e3b1f] bg-[#120d09] p-5 shadow-[0_24px_80px_rgba(0,0,0,0.65)]">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-xl font-bold text-[#ffd66b]">{t("title")}</h3>
          <label className="flex items-center gap-2 text-xs font-semibold text-[#f8f2e6]/70">
            <span>{t("languageLabel")}</span>
            <select
              value={termsLanguage}
              onChange={(event) => setTermsLanguage(normalizeLanguage(event.target.value))}
              disabled={loading}
              className="rounded-md border border-[#8b5724] bg-[#1a1008] px-2 py-1.5 text-xs font-semibold text-[#ffd66b] outline-none transition hover:border-[#ffd66b] disabled:opacity-60"
            >
              {LANGUAGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.shortLabel} - {option.nativeLabel}
                </option>
              ))}
            </select>
          </label>
        </div>

        <p className="mt-2 text-sm text-[#f8f2e6]/80">{intro}</p>

        <div
          className="mt-4 max-h-[52vh] overflow-y-auto rounded border border-[#3c2414] bg-[#070604]/80 p-4 select-text scrollbar-gamified"
          style={{ userSelect: "text", WebkitUserSelect: "text" }}
        >
          {termsBlocks.map((block, index) => {
            if (block.type === "heading") {
              const isMainHeading = block.level === 1 && index === 0;
              return (
                <h4
                  key={`${mode}-heading-${index}`}
                  className={
                    isMainHeading
                      ? "mb-3 text-base font-bold leading-snug text-[#ffd66b]"
                      : "mb-2 mt-4 text-sm font-bold leading-snug text-[#ffd66b]"
                  }
                >
                  {renderInlineMarkdown(block.text)}
                </h4>
              );
            }
            if (block.type === "list") {
              return (
                <ul
                  key={`${mode}-list-${index}`}
                  className="mb-3 list-disc space-y-1 pl-5 text-sm leading-relaxed text-[#f8f2e6]/82"
                >
                  {block.items.map((item, itemIndex) => (
                    <li key={`${mode}-list-${index}-${itemIndex}`}>
                      {renderInlineMarkdown(item)}
                    </li>
                  ))}
                </ul>
              );
            }
            return (
              <p key={`${mode}-paragraph-${index}`} className="mb-3 text-sm leading-relaxed text-[#f8f2e6]/82 last:mb-0">
                {renderInlineMarkdown(block.text)}
              </p>
            );
          })}
        </div>

        <label className="mt-4 flex cursor-pointer items-start gap-2 text-sm text-[#f8f2e6]">
          <input
            type="checkbox"
            checked={accepted}
            onChange={(event) => setAccepted(event.target.checked)}
            disabled={loading}
            className="mt-0.5 h-4 w-4 accent-[#f7b531]"
          />
          <span>{acceptLabel}</span>
        </label>

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          {allowCancel ? (
            <button
              type="button"
              className="rounded-md border border-[#3c2414] bg-[#070604] px-4 py-2 text-sm text-[#f8f2e6] transition hover:border-[#f7b531]/75"
              onClick={onCancel}
              disabled={loading}
            >
              {t("cancel")}
            </button>
          ) : null}

          <button
            type="button"
            className={`rounded-md px-4 py-2 text-sm font-semibold ${
              accepted && !loading
                ? "bg-gradient-to-b from-[#ffd66b] to-[#f7a928] text-[#150d05] hover:brightness-110"
                : "cursor-not-allowed bg-[#2b1c0f] text-[#f8f2e6]/45 opacity-70"
            }`}
            onClick={onAccept}
            disabled={!accepted || loading}
          >
            {t("accept")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsAcceptanceModal;
